import { useState, useEffect, useCallback } from 'react';

type ForgotPasswordVariant = 'default' | 'warning' | 'primary-full';

interface EscalationState {
  failureCount: number;
  forgotPasswordVariant: ForgotPasswordVariant;
  forcePasswordVisible: boolean;
  remainingCooldown: number;
  suggestReset: boolean;
  showTeacherHelp: boolean;
  incrementFailure: () => void;
  resetFailures: () => void;
}

function getCooldownSeconds(count: number): number {
  if (count >= 15) return 30;
  if (count >= 8) return 15;
  if (count >= 5) return 5;
  return 0;
}

export function useLoginFailureEscalation(): EscalationState {
  const [failureCount, setFailureCount] = useState(0);
  const [remainingCooldown, setRemainingCooldown] = useState(0);

  const incrementFailure = useCallback(() => {
    setFailureCount((prev) => {
      const next = prev + 1;
      const cooldown = getCooldownSeconds(next);
      if (cooldown > 0) {
        setRemainingCooldown(cooldown);
      }
      return next;
    });
  }, []);

  const resetFailures = useCallback(() => {
    setFailureCount(0);
    setRemainingCooldown(0);
  }, []);

  // Cooldown countdown timer
  useEffect(() => {
    if (remainingCooldown <= 0) return;

    const interval = setInterval(() => {
      setRemainingCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [remainingCooldown]);

  // Derive state from failure count
  let forgotPasswordVariant: ForgotPasswordVariant = 'default';
  if (failureCount >= 3) {
    forgotPasswordVariant = 'primary-full';
  } else if (failureCount >= 2) {
    forgotPasswordVariant = 'warning';
  }

  const forcePasswordVisible = failureCount >= 3;
  const suggestReset = failureCount >= 5;
  const showTeacherHelp = failureCount >= 8;

  return {
    failureCount,
    forgotPasswordVariant,
    forcePasswordVisible,
    remainingCooldown,
    suggestReset,
    showTeacherHelp,
    incrementFailure,
    resetFailures,
  };
}
